import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createContentCategoryApi,
  createContentSubCategoryApi,
  createPostApi,
  deleteContentCategoryApi,
  deleteContentSubCategoryApi,
  deletePostApi,
  getContentCategoriesApi,
  getPostsApi,
  updateContentCategoryApi,
  updatePostApi,
} from "./contentApi";

export const getContentCategories = createAsyncThunk(
  "content/getCategories",
  async (_, thunkAPI) => {
    try {
      const res = await getContentCategoriesApi();
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const createContentCategory = createAsyncThunk(
  "content/createCategory",
  async (data, thunkAPI) => {
    try {
      const res = await createContentCategoryApi(data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const updateContentCategory = createAsyncThunk(
  "content/updateCategory",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await updateContentCategoryApi(id, data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const createContentSubCategory = createAsyncThunk(
  "content/createSubCategory",
  async (data, thunkAPI) => {
    try {
      const res = await createContentSubCategoryApi(data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const deleteContentCategory = createAsyncThunk(
  "content/deleteCategory",
  async (categoryId, thunkAPI) => {
    try {
      await deleteContentCategoryApi(categoryId);
      return categoryId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const deleteContentSubCategory = createAsyncThunk(
  "content/deleteSubCategory",
  async (subCategoryId, thunkAPI) => {
    try {
      await deleteContentSubCategoryApi(subCategoryId);
      return subCategoryId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const getPosts = createAsyncThunk(
  "content/getPosts",
  async (params, thunkAPI) => {
    try {
      const res = await getPostsApi(params);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const createPost = createAsyncThunk(
  "content/createPost",
  async (data, thunkAPI) => {
    try {
      const res = await createPostApi(data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const updatePost = createAsyncThunk(
  "content/updatePost",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await updatePostApi(id, data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const deletePost = createAsyncThunk(
  "content/deletePost",
  async (postId, thunkAPI) => {
    try {
      await deletePostApi(postId);
      return postId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

const slice = createSlice({
  name: "content",
  initialState: {
    categories: [],
    posts: [],
    postsTotal: 0,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContentCategories.fulfilled, (state, action) => {
        state.categories = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(createContentCategory.fulfilled, (state, action) => {
        if (action.payload) state.categories.unshift(action.payload);
      })
      .addCase(updateContentCategory.fulfilled, (state, action) => {
        const i = state.categories.findIndex((category) => category.id === action.payload?.id);
        if (i !== -1) state.categories[i] = action.payload;
      })
      .addCase(deleteContentCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter((c) => c.id !== action.payload);
      })
      .addCase(createContentSubCategory.fulfilled, (state, action) => {
        const categoryId = action.payload?.categoryId || action.payload?.category?.id;
        const index = state.categories.findIndex((c) => c.id === categoryId);
        if (index !== -1) {
          const subCategories = state.categories[index].subCategories || state.categories[index].subcategories || [];
          state.categories[index].subCategories = [action.payload, ...subCategories];
        }
      })
      .addCase(deleteContentSubCategory.fulfilled, (state, action) => {
        state.categories = state.categories.map((category) => {
          const subCategories = category.subCategories || category.subcategories || [];
          return {
            ...category,
            subCategories: subCategories.filter((s) => s.id !== action.payload),
          };
        });
      })
      .addCase(getPosts.fulfilled, (state, action) => {
        const payload = action.payload;
        state.posts = Array.isArray(payload?.posts)
          ? payload.posts
          : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];
        state.postsTotal = payload?.total || payload?.count || state.posts.length;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        if (action.payload) state.posts.unshift(action.payload);
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        const i = state.posts.findIndex((post) => post.id === action.payload?.id);
        if (i !== -1) state.posts[i] = action.payload;
      })
      .addCase(deletePost.fulfilled, (state, action) => {
        state.posts = state.posts.filter((post) => post.id !== action.payload);
      });
  },
});

export default slice.reducer;
